/**
 * @module features/devices/components/DeviceFilterBar
 * @description Debounced search + type/status filters using shadcn Select and segmented pills.
 * Status pills scroll horizontally on mobile; all targets are >= 44px on touch viewports.
 */
import * as React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { DEVICE_STATUSES, DeviceStats, DeviceTypeDescriptor } from "../types";
import { statusLabel } from "./DeviceStatusBadge";

interface DeviceFilterBarProps {
  search: string;
  status: string;
  deviceType: string;
  types: DeviceTypeDescriptor[];
  stats?: DeviceStats;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onTypeChange: (value: string) => void;
}

export function DeviceFilterBar({
  search,
  status,
  deviceType,
  types,
  stats,
  onSearchChange,
  onStatusChange,
  onTypeChange,
}: DeviceFilterBarProps) {
  const [localSearch, setLocalSearch] = React.useState(search);

  // Debounce so we don't query on every keystroke.
  React.useEffect(() => {
    if (localSearch === search) return;
    const handle = setTimeout(() => {
      onSearchChange(localSearch);
    }, 300);
    return () => clearTimeout(handle);
  }, [localSearch, search, onSearchChange]);

  React.useEffect(() => {
    setLocalSearch((prev) => (prev !== search ? search : prev));
  }, [search]);

  const statuses = [{ value: "all", label: "All" }].concat(
    DEVICE_STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))
  );

  return (
    <div className="space-y-3 bg-card dark:bg-zinc-950 p-3 sm:p-3.5 rounded-2xl border border-border/70 dark:border-zinc-800/80 shadow-xs">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            aria-hidden="true"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
          />
          <Input
            type="search"
            aria-label="Search devices"
            placeholder="Search by name, MAC address, or device ID..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="pl-10 pr-10 h-11 sm:h-10 text-base sm:text-sm bg-muted/40 dark:bg-zinc-900 rounded-xl"
          />
          {localSearch && (
            <button
              type="button"
              onClick={() => {
                setLocalSearch("");
                onSearchChange("");
              }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* shadcn Select for Device Type */}
        <div className="w-full sm:w-[210px] shrink-0">
          <Select value={deviceType} onValueChange={onTypeChange}>
            <SelectTrigger
              aria-label="Filter by device type"
              className="h-11 sm:h-10 bg-muted/40 dark:bg-zinc-900 border-border/70 dark:border-zinc-800 rounded-xl font-medium"
            >
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span>All types</span>
                {stats?.total_devices !== undefined && (
                  <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                    ({stats.total_devices})
                  </span>
                )}
              </SelectItem>
              {types.map((t) => {
                const count = stats?.by_type?.[t.type_id];
                return (
                  <SelectItem key={t.type_id} value={t.type_id}>
                    <span>{t.label}</span>
                    {count !== undefined && (
                      <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                        ({count})
                      </span>
                    )}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Segmented Filter Pills */}
      <div
        role="group"
        aria-label="Filter by status"
        className="flex items-center gap-1 p-1 bg-muted/60 dark:bg-zinc-900 rounded-xl border border-border/40 dark:border-zinc-800/80 overflow-x-auto"
      >
        {statuses.map((s) => {
          const active = status === s.value;
          const count =
            s.value === "all"
              ? stats?.total_devices
              : stats?.by_status?.[s.value as keyof typeof stats.by_status];

          return (
            <button
              key={s.value}
              type="button"
              aria-pressed={active}
              onClick={() => onStatusChange(s.value)}
              className={cn(
                "min-h-[40px] sm:min-h-0 px-3.5 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-all duration-150 inline-flex items-center gap-1.5",
                active
                  ? "bg-card dark:bg-zinc-800 text-foreground shadow-xs border border-border/40 dark:border-zinc-700"
                  : "text-muted-foreground hover:text-foreground hover:bg-card/40"
              )}
            >
              <span>{s.label}</span>
              {count !== undefined && (
                <span
                  className={cn(
                    "font-mono text-[11px] px-1.5 py-0.2 rounded font-medium",
                    active
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
